<script setup lang="ts">
// Entry: demo mode (?demo, dev builds or VITE_ENABLE_DEMO=1 only) renders the reference sample data in memory
// for visual acceptance; otherwise the signed-in, offline-first workspace.
import { onBeforeUnmount, shallowRef, ref, watch } from 'vue';
import PanesApp from '@/modules/panes/PanesApp.vue';
import SignIn from '@/modules/auth/SignIn.vue';
import ResetPassword from '@/modules/auth/ResetPassword.vue';
import InviteAccept from '@/modules/auth/InviteAccept.vue';
import { inviteTokenFromPath } from '@/app/invite';
import { PanesController } from '@/modules/panes/controller';
import { localPrefs } from '@/modules/panes/ports';
import { buildDemoWorkspace } from '@/dev/demoFixture';
import { demoWorkspace } from '@/dev/demoWorkspace';
import { applyLang, t } from '@/app/i18n';
import { boot, sessionFromMe } from '@/app/session';
import { brand, hostStatus, loadBrand } from '@/app/brand';
import { startWorkspace, type Runtime } from '@/app/workspaceRuntime';
import type { Me } from '@/api/auth';
import AdminApp from '@/modules/admin/AdminApp.vue';
import { AdminController, type View } from '@/modules/admin/controller';
import { httpAdminPort } from '@/modules/admin/httpAdminPort';
import { demoAdminPort } from '@/dev/demoAdmin';
import ReadOnlyView from '@/modules/share/ReadOnlyView.vue';
import { shareTokenFromPath } from '@/api/publicLinks';

const params = new URLSearchParams(location.search);
const demoAllowed = import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEMO === '1';
const ctrl = shallowRef<PanesController | null>(null);
const view = ref<'loading' | 'app' | 'signin' | 'maintenance' | 'invite' | 'offline_unavailable' | 'error' | 'host_not_served' | 'readonly'>('loading');
const inviteToken = inviteTokenFromPath(location.pathname);
// Password reset link from email (`/app/?reset=TOKEN`, admin console or self-service): shown before anything else.
const resetToken = ref(new URLSearchParams(location.search).get('reset'));
const onResetDone = (): void => { resetToken.value = null; location.assign('/'); };
const brandName = ref(document.title || 'TaskCool');
const accent = ref(localPrefs.get('taskpanes-v3-accent') || '#5b8def');
let runtime: Runtime | null = null;
// /admin (and /admin/...) renders the admin console instead of the panes app (same SPA, same origin).
const isAdmin = /^\/admin(\/|$)/.test(location.pathname);
// /s/{token}: public read-only link viewer (design v6 F4). Unauthenticated: no session bootstrap, no brand cache,
// no IndexedDB, no sync engine — it only calls the public link API.
const shareToken = shareTokenFromPath(location.pathname);
const adminCtrl = shallowRef<AdminController | null>(null);
if (!isAdmin) { try { applyLang((JSON.parse(localPrefs.get('taskpanes-v3-prefs') || '{}') as { lang?: string }).lang === 'fa' ? 'fa' : 'en'); } catch { applyLang('en'); } }

async function enter(session: Awaited<ReturnType<typeof sessionFromMe>>): Promise<void> {
  runtime?.dispose();
  brandName.value = brand.display_name || session.brandName;
  runtime = await startWorkspace(session, () => { runtime?.dispose(); runtime = null; ctrl.value = null; view.value = 'signin'; });
  ctrl.value = runtime.ctrl;
  if (import.meta.env.DEV) (window as unknown as { __tpCtrl: unknown }).__tpCtrl = runtime.ctrl;
  view.value = 'app';
}
async function onSignedIn(m: Me): Promise<void> { await enter(await sessionFromMe(m)); }

if (shareToken) {
  view.value = 'readonly';
} else if (isAdmin) {
  if (demoAllowed && params.has('demo')) {
    // DEV/DEMO ONLY: the Admin Panel prototype's sample data, in memory (frontend/src/dev/demoAdmin.ts).
    adminCtrl.value = new AdminController(demoAdminPort(), { startView: (params.get('view') as View | null) || 'overview' });
  } else {
    const c = new AdminController(httpAdminPort());
    adminCtrl.value = c;
    void loadBrand().then(b => { c.props.accent = b.accent || c.props.accent; }).catch(() => undefined);
  }
  if (import.meta.env.DEV) (window as unknown as { __tpAdmin: unknown }).__tpAdmin = adminCtrl.value;
} else if (demoAllowed && params.has('demo')) {
  // The reference bundle's ReactDOM draws Math.random() twice at load before seeding its sample data;
  // skip the same two draws so a seeded PRNG yields identical demo data (docs/design-acceptance.md).
  Math.random(); Math.random();
  const data = buildDemoWorkspace();
  const c = new PanesController({}, demoWorkspace(data.users, () => c.setState({ signedOut: true })), localPrefs, data);
  ctrl.value = c;
  if (import.meta.env.DEV) (window as unknown as { __tpCtrl: unknown }).__tpCtrl = c;
  view.value = 'app';
} else {
  start();
}
function start(): void {
  loadBrand().then(b => { if (hostStatus.notServed) { view.value = 'host_not_served'; return undefined; } brandName.value = b.display_name; if (!localPrefs.get('taskpanes-v3-accent')) accent.value = b.accent; if (!localPrefs.get('taskpanes-v3-prefs')) applyLang(b.lang === 'fa' ? 'fa' : 'en'); return inviteToken ? null : boot(); }).then(r => {
    if (r === undefined) return;
    if (r === null) { view.value = 'invite'; return; }
    if (r.kind === 'ready') return enter(r.session);
    view.value = r.kind === 'signed_out' ? 'signin' : r.kind === 'maintenance' ? 'maintenance' : 'offline_unavailable';
  }).catch(() => { view.value = 'error'; });
}
// Server unreachable at start and nothing cached on this device (e.g. right after signing out): not a dead end —
// retry when the browser reports a connection, every 10 s, or on "Try again".
let retryT: ReturnType<typeof setInterval> | null = null;
const retry = () => { if (view.value === 'offline_unavailable') start(); };
watch(view, v => {
  if (v === 'offline_unavailable' && !retryT) { retryT = setInterval(retry, 10_000); window.addEventListener('online', retry); }
  else if (v !== 'offline_unavailable' && retryT) { clearInterval(retryT); retryT = null; window.removeEventListener('online', retry); }
});
onBeforeUnmount(() => { runtime?.dispose(); if (retryT) clearInterval(retryT); window.removeEventListener('online', retry); });
</script>

<template>
  <ReadOnlyView v-if="view === 'readonly' && shareToken" :token="shareToken" />
  <AdminApp v-else-if="adminCtrl" :ctrl="adminCtrl" />
  <ResetPassword v-else-if="resetToken && view !== 'loading'" :token="resetToken" :brand-name="brandName" :accent="accent" @done="onResetDone" />
  <PanesApp v-else-if="view === 'app' && ctrl" :ctrl="ctrl" />
  <InviteAccept v-else-if="view === 'invite' && inviteToken" :token="inviteToken" :brand-name="brandName" :accent="accent" @done="onSignedIn" />
  <SignIn v-else-if="view === 'signin'" :brand-name="brandName" :accent="accent" :notice="brand.maintenance ? t('TaskCool is being updated. Try again in a few minutes.') : undefined" @signed-in="onSignedIn" />
  <div v-else :style="s('height:100vh; display:grid; place-items:center; padding:24px; text-align:center; color:var(--n9c9da3, #9c9da3); font-size:14px;')" role="status">
    <div>
      {{ view === 'maintenance' ? t('TaskCool is being updated. Try again in a few minutes.') : view === 'offline_unavailable' ? t('You are offline. Open the app once while online to use it offline on this device.') : view === 'error' ? t('Something went wrong. Reload to try again.') : view === 'host_not_served' ? t('This address is not set up yet. If you manage this site, finish verifying the domain.') : '' }}
      <div v-if="view === 'offline_unavailable'" :style="s('margin-top:14px;')"><button data-boot-retry @click="retry" :style="s('height:34px; padding:0 16px; border:none; border-radius:8px; background:var(--n34353a, #34353a); color:var(--ne7e7ea, #e7e7ea); font-family:inherit; font-size:14px; cursor:pointer;')">{{ t('Try again') }}</button></div>
    </div>
  </div>
</template>
